// Outlier review — surface a suspicious cluster of readings for a human decision.
//
// A utility export can be parsed perfectly and still be unusable. The National
// Grid file that prompted this contains a meter register rollover on 2025-04-18:
// twelve readings between 207 and 1,789 kW, one reading of -7,047 kW, and then a
// 21.6x cliff down to 9.58 kW — which is the real peak for this service. Because
// NEC 220.87 is a maximum-demand method, that single day decides the entire
// calculation.
//
// The app used to reject any peak above a fixed 500 kW ceiling and tell the user
// to "verify your file format and re-upload", which could not work: the file is
// fine, re-uploading reproduces it exactly, and the data was discarded either
// way. This module instead measures what makes the cluster look wrong, so the
// user can be shown the numbers and asked what to do with them.
//
// Nothing here decides anything. It reports; the user chooses.

import type { ParsedDataPoint } from './types';

// ─── Tunable thresholds ──────────────────────────────────────────────────────
/** Below this, ranked gaps are noise rather than evidence. */
const MIN_READINGS_FOR_REVIEW = 100;
/** A cluster larger than this share of the file is the data, not an anomaly. */
const MAX_CLUSTER_FRACTION = 0.01;
/** Hard ceiling on cluster size for very large files. */
const MAX_CLUSTER_READINGS = 200;
/** The drop below the cluster must be at least this many times. */
const MIN_CLIFF_RATIO = 4;
/** Above this share, negative readings are generation/net metering, not corruption. */
const MAX_NEGATIVE_FRACTION = 0.005;

export interface OutlierReview {
  /** Timestamps of every reading the user would drop by excluding. */
  excludedTimestamps: string[];
  /** Readings far above the rest of the file. */
  highCount: number;
  maxKw: number;
  /** Lowest reading inside the cluster. */
  clusterMinKw: number;
  /** Highest reading outside the cluster — the peak if the cluster is excluded. */
  nextKw: number;
  /** How many times clusterMinKw exceeds nextKw. */
  cliffRatio: number;
  /** Calendar dates (YYYY-MM-DD, UTC) the cluster falls on. */
  dates: string[];
  /** Rare negative readings, which a meter correction produces and a load does not. */
  negativeCount: number;
  minNegativeKw: number | null;
}

function utcDate(timestamp: string): string {
  return timestamp.slice(0, 10);
}

/**
 * Find a cluster of readings separated from the rest of the file by a large gap.
 *
 * Ranked-gap rather than a fixed kW ceiling: a threshold has to be wrong for
 * somebody. 500 kW is absurd for a house and unremarkable for a small commercial
 * service, whereas "twelve readings sit 21x above the thirteenth" is evidence
 * about this file regardless of the service size.
 *
 * Returns null when nothing stands out — the ordinary case.
 */
export function detectOutlierReview(points: ParsedDataPoint[]): OutlierReview | null {
  const finite = points.filter(p => Number.isFinite(p.kw));
  if (finite.length < MIN_READINGS_FOR_REVIEW) return null;

  // Negative readings: a meter correction, unless there are enough of them to be
  // solar export, in which case they are the customer's generation and not ours
  // to call an anomaly.
  const negatives = finite.filter(p => p.kw < 0);
  const negativesAreAnomalous =
    negatives.length > 0 && negatives.length / finite.length <= MAX_NEGATIVE_FRACTION;

  const sorted = [...finite].sort((a, b) => b.kw - a.kw);
  const maxSplit = Math.min(
    MAX_CLUSTER_READINGS,
    Math.floor(finite.length * MAX_CLUSTER_FRACTION)
  );

  let bestSplit = 0;
  let bestRatio = 0;
  for (let k = 1; k <= maxSplit && k < sorted.length; k++) {
    const inside = sorted[k - 1].kw;
    const outside = sorted[k].kw;
    if (outside <= 0 || inside <= 0) break;
    const ratio = inside / outside;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      bestSplit = k;
    }
  }

  const hasCluster = bestSplit > 0 && bestRatio >= MIN_CLIFF_RATIO;
  if (!hasCluster && !negativesAreAnomalous) return null;

  const cluster = hasCluster ? sorted.slice(0, bestSplit) : [];
  const excluded = [...cluster, ...(negativesAreAnomalous ? negatives : [])];
  const dates = [...new Set(excluded.map(p => utcDate(p.timestamp)))].sort();

  return {
    excludedTimestamps: excluded.map(p => p.timestamp),
    highCount: cluster.length,
    maxKw: cluster.length > 0 ? cluster[0].kw : sorted[0].kw,
    clusterMinKw: cluster.length > 0 ? cluster[cluster.length - 1].kw : 0,
    nextKw: hasCluster ? sorted[bestSplit].kw : sorted[0].kw,
    cliffRatio: hasCluster ? bestRatio : 0,
    dates,
    negativeCount: negativesAreAnomalous ? negatives.length : 0,
    minNegativeKw: negativesAreAnomalous
      ? negatives.reduce((m, p) => Math.min(m, p.kw), 0)
      : null,
  };
}

function formatDateList(dates: string[]): string {
  if (dates.length === 1) return dates[0];
  if (dates.length === 2) return `${dates[0]} and ${dates[1]}`;
  return `${dates.slice(0, -1).join(', ')} and ${dates[dates.length - 1]}`;
}

/**
 * Plain-language summary of what stands out, for the decision card. States the
 * facts and stops — the recommendation is the user's to make.
 *
 * `showKw` is the paywall: the card opens on the Existing Load tab before the
 * project is unlocked, and the measured peak is paid content. Locked, the
 * summary describes the cluster by count, dates and ratio; unlocked, it names
 * the kilowatts.
 */
export function describeOutlierReview(review: OutlierReview, showKw = false): string {
  const parts: string[] = [];

  if (review.highCount > 0) {
    const readings = review.highCount === 1 ? 'reading' : 'readings';
    parts.push(
      showKw
        ? `${review.highCount} ${readings} fall between ${review.clusterMinKw.toFixed(1)} kW and ` +
          `${review.maxKw.toFixed(1)} kW, all on ${formatDateList(review.dates)}. ` +
          `The next highest reading in the file is ${review.nextKw.toFixed(2)} kW — ` +
          `${review.cliffRatio.toFixed(0)} times lower.`
        : `${review.highCount} ${readings}, all on ${formatDateList(review.dates)}, stand about ` +
          `${review.cliffRatio.toFixed(0)} times above the next highest reading in the file.`
    );
  }

  if (review.negativeCount > 0 && review.minNegativeKw !== null) {
    const readings = review.negativeCount === 1 ? 'reading' : 'readings';
    parts.push(
      `The file also contains ${review.negativeCount} negative ${readings}, as low as ` +
      `${review.minNegativeKw.toFixed(1)} kW. A load cannot draw negative power; this is ` +
      'characteristic of a meter register correction.'
    );
  }

  return parts.join(' ');
}

/**
 * AHJ-facing note recording what the user decided. Written to the project's data
 * quality notes either way — accepting the readings is as much a decision as
 * excluding them, and the report should say which was made.
 */
export function buildOutlierDecisionNote(
  review: OutlierReview,
  decision: 'excluded' | 'accepted'
): string {
  const readings = review.excludedTimestamps.length;
  const label = readings === 1 ? 'reading' : 'readings';
  const where = formatDateList(review.dates);

  // No kilowatt values: this note is stored in the data quality notes, which
  // show on the Existing Load tab before unlock. The ratio and the dates carry
  // the record; the peak itself prints elsewhere on the report.
  const ratio = review.cliffRatio > 0 ? `about ${review.cliffRatio.toFixed(0)} times` : 'well';

  if (decision === 'excluded') {
    return (
      `${readings} ${label} on ${where} were excluded from this calculation by the preparer. ` +
      `They stood ${ratio} above the next-highest reading elsewhere in the file` +
      (review.negativeCount > 0 ? ', and included negative demand values' : '') +
      '. Peak demand is calculated from the remaining readings.'
    );
  }

  return (
    `${readings} ${label} on ${where} were reviewed by the preparer and retained. ` +
    `They stand ${ratio} above the next-highest reading elsewhere in the file` +
    (review.negativeCount > 0 ? ', and include negative demand values' : '') +
    '. Peak demand is calculated from all readings including these.'
  );
}
